import React, { useRef, forwardRef, useState, useEffect } from "react";
import ReactToPrint from "react-to-print";
import Moment from "react-moment";
import Button from "components/CustomButtons/Button.js";
import { Visibility as VisibilityIcon } from "components/MaterialSymbol/MaterialIcons";
import { Print as PrintIcon } from "components/MaterialSymbol/MaterialIcons";
import { isShippingSameAsBilling } from "helpers";

import OrderDataService from "services/OrderService";

const financial = (number, region = "USA", isSaleTax = false) => {
    const minimumFractionDigits = isSaleTax && region === "CAN" ? 3 : 2;

    return Number.parseFloat(number).toFixed(minimumFractionDigits);
};
const getTranslation = (text, locale) => text;
const cellStyle = {
    borderBottom: "1px solid rgb(221, 221, 221)",
    padding: 5,
};

const ComponentToPrint = forwardRef((props, ref) => {
    const {
        orderNumber,
        createdAt,
        requiredDate,
        salesman,
        billCompany,
        shipCompany,
        payTerms,
        products = [],
        dealerCompany,
        dealerAddress1,
        dealerCity,
        dealerState,
        dealerZip,
    } = props.order;
    const locale = props.locale;
    const isInternational = Number(props.order.internationalOrders) === 1;
    const useBillingAsShipping = isShippingSameAsBilling(props.order);
    const showBillingAddress = !isInternational;
    const showShippingSection = !isInternational;
    const isSingleItem = products.length === 1;
    const firstP = products[0] || {};
    const allItemsSame = products.length > 0 && products.every(p =>
        p.trackingNumber === firstP.trackingNumber &&
        p.shippingCarrier === firstP.shippingCarrier
    );

    const showHeaderTracking = allItemsSame;

    const headerCarrier = showHeaderTracking ? (firstP.shippingCarrier || props.order.shippingCarrier) : null;
    const headerTracking = showHeaderTracking ? (firstP.trackingNumber || props.order.trackingNumber) : null;
    const effectiveShipCompany = useBillingAsShipping ? props.order.billCompany : props.order.shipCompany;
    const effectiveShipAddress1 = useBillingAsShipping ? props.order.billAddress1 : props.order.shipAddress1;
    const effectiveShipAddress2 = useBillingAsShipping ? props.order.billAddress2 : props.order.shipAddress2;
    const effectiveShipState = useBillingAsShipping ? props.order.billState : props.order.shipState;
    const effectiveShipCity = useBillingAsShipping ? props.order.billCity : props.order.shipCity;
    const effectiveShipZip = useBillingAsShipping ? props.order.billZip : props.order.shipZip;
    const effectiveShipCountry = useBillingAsShipping ? props.order.billCountry : props.order.shipCountry;
    const effectiveShipPhone = useBillingAsShipping ? props.order.billPhone : props.order.shipPhone;
    const effectiveShipEmail = useBillingAsShipping ? props.order.billEmail : props.order.shipEmail;

    return (
        <div ref={ref} style={{ margin: 20 }}>
            {!props.order?.pgatewayId ? (
                <div style={{ marginBottom: 10 }}>
                    {getTranslation(
                        "We are pleased to inform you that we have received your parts request. Our #1 priority is locating your requested items as quickly as possible. Once we are ready to process your order we will contact you to arrange payment and discuss shipping options.",
                        locale
                    )}
                </div>
            ) : null}
            <div>
                <table style={{ width: "100%" }} border="0">
                    <tbody>
                        <tr>
                            <td>
                                {getTranslation("Company", locale)}: {dealerCompany}
                            </td>
                            <td style={{ textAlign: "right" }}>
                                {getTranslation("Order number", locale)}: {orderNumber}
                            </td>
                        </tr>
                        <tr>
                            <td>
                                {getTranslation("Address", locale)}: {dealerAddress1}
                                {dealerCity && `, ${dealerCity}`}
                                {dealerState && `, ${dealerState}`}
                                {dealerZip && ` ${dealerZip}`}
                            </td>
                            <td style={{ textAlign: "right" }}>
                                {getTranslation("Creation date", locale)}:{" "}
                                <Moment locale={locale} format="MM/DD/YYYY">
                                    {createdAt}
                                </Moment>
                            </td>
                        </tr>
                        <tr style={{ display: "none" }}>
                            <td style={{ textAlign: "right" }}>
                                {getTranslation("Required at", locale)}:{" "}
                                <Moment locale={locale} format="MM/DD/YYYY">
                                    {requiredDate}
                                </Moment>
                            </td>
                        </tr>
                        <tr style={{ display: "none" }}>
                            <td style={{ textAlign: "right" }}>
                                {getTranslation("Salesman", locale)}: {salesman}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div style={{ marginTop: 50 }}>
                <table style={{ width: "100%" }} border="0">
                    <tbody>
                        <tr>
                            <td width="50%">
                                {getTranslation("Bill to", locale)}: {billCompany}
                            </td>
                            <td style={{ textAlign: "right" }}>
                                {showShippingSection && (
                                    props.order.isPickupAtDealership === 1 ? (
                                        <strong>{getTranslation("Pick up at dealership", locale)}</strong>
                                    ) : (
                                        <>{getTranslation("Ship to", locale)}: {effectiveShipCompany}</>
                                    )
                                )}
                            </td>
                        </tr>
                        {showBillingAddress && (
                            <>
                                <tr>
                                    <td>
                                        {getTranslation("Address line 1", locale)}:{" "}
                                        {props.order.billAddress1}
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                        {props.order.isPickupAtDealership !== 1 && (
                                            <>
                                                {getTranslation("Address line 1", locale)}:{" "}
                                                {effectiveShipAddress1}
                                            </>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        {getTranslation("Address line 2", locale)}:{" "}
                                        {props.order.billAddress2}
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                        {props.order.isPickupAtDealership !== 1 && (
                                            <>
                                                {getTranslation("Address line 2", locale)}:{" "}
                                                {effectiveShipAddress2}
                                            </>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        {getTranslation("State", locale)}: {props.order.billState}
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                        {props.order.isPickupAtDealership !== 1 && (
                                            <>
                                                {getTranslation("State", locale)}: {effectiveShipState}
                                            </>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        {getTranslation("City", locale)}: {props.order.billCity}
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                        {props.order.isPickupAtDealership !== 1 && (
                                            <>
                                                {getTranslation("City", locale)}: {effectiveShipCity}
                                            </>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        {getTranslation("ZIP", locale)}: {props.order.billZip}
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                        {props.order.isPickupAtDealership !== 1 && (
                                            <>
                                                {getTranslation("ZIP", locale)}: {effectiveShipZip}
                                            </>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        {getTranslation("Country", locale)}: {props.order.billCountry}
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                        {props.order.isPickupAtDealership !== 1 && (
                                            <>
                                                {getTranslation("Country", locale)}: {effectiveShipCountry}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            </>
                        )}
                        <tr>
                            <td>
                                {getTranslation("Phone", locale)}: {props.order.billPhone}
                            </td>
                            <td style={{ textAlign: "right" }}>
                                {showShippingSection && props.order.isPickupAtDealership !== 1 && (
                                    <>
                                        {getTranslation("Phone", locale)}: {effectiveShipPhone}
                                    </>
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td>
                                {getTranslation("Email", locale)}: {props.order.billEmail}
                            </td>
                            <td style={{ textAlign: "right" }}>
                                {showShippingSection && props.order.isPickupAtDealership !== 1 && (
                                    <>{getTranslation("Email", locale)}: {effectiveShipEmail}</>
                                )}
                            </td>
                        </tr>
                        {showShippingSection && props.order.isPickupAtDealership !== 1 && (headerCarrier || headerTracking) && (
                            <tr>
                                <td></td>
                                <td style={{ textAlign: "right" }}>
                                    {headerCarrier && (
                                        <div>
                                            {getTranslation("Carrier", locale)}: {headerCarrier}
                                        </div>
                                    )}
                                    {headerTracking && (
                                        <div>
                                            {getTranslation("Tracking Number", locale)}: {headerTracking}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div style={{ marginTop: 50 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }} border="0">
                    <thead>
                        <tr>
                            <th width="50%" style={cellStyle}>
                                {getTranslation("Instructions", locale)}
                            </th>
                            <th style={cellStyle}>{getTranslation("Terms", locale)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={cellStyle}>
                                {props.order.instructions}
                                {props.order.vinToOrder && (
                                    <>
                                        {props.order.instructions && <br />}
                                        VIN: {props.order.vinToOrder}
                                    </>
                                )}
                            </td>
                            <td style={{ ...cellStyle, textAlign: "right" }}>{payTerms}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div style={{ marginTop: 50 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }} border="0">
                    <thead>
                        <tr>
                            <th style={cellStyle}>Part number(s)</th>
                            <th style={cellStyle}>{getTranslation("Description", locale)}</th>
                            <th style={cellStyle}>{getTranslation("Price", locale)}</th>
                            <th style={cellStyle}>{getTranslation("Quantity", locale)}</th>
                            <th style={cellStyle}>Subtotal</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((p, i) => (
                            <tr key={i}>
                                <td style={{ ...cellStyle, textAlign: "center" }}>
                                    {p.partNumber}
                                </td>
                                <td style={cellStyle}>
                                    {p.description}
                                    <br />
                                    {p.year} {p.makeSlug} {p.modelSlug}
                                    {p.vin ? (
                                        <>
                                            <br />
                                            VIN: {p.vin}
                                        </>
                                    ) : (
                                        ""
                                    )}
                                    {!showHeaderTracking && (p.trackingNumber || p.shippingCarrier) && (
                                        <div style={{ marginTop: 5, fontSize: 12, fontStyle: "italic", color: "#666" }}>
                                            Shipment: {p.shippingCarrier || "N/A"}{p.trackingNumber ? ` - ${p.trackingNumber}` : ""}
                                        </div>
                                    )}
                                </td>
                                <td style={{ ...cellStyle, textAlign: "right" }}>
                                    ${financial(p.price)}
                                </td>
                                <td style={{ ...cellStyle, textAlign: "center" }}>{p.qty}</td>
                                <td style={{ ...cellStyle, textAlign: "right" }}>
                                    ${financial(p.price * p.qty)}
                                </td>
                            </tr>
                        ))}
                        <tr>
                            <td colSpan={3}></td>
                            <td style={{ padding: 5, textAlign: "right" }}>Subtotal</td>
                            <td style={{ padding: 5, textAlign: "right" }}>
                                ${financial(props.order.subtotal)}
                            </td>
                        </tr>
                        {props.order.shippingAmount > 0 && props.order.isPickupAtDealership !== 1 && (
                            <tr>
                                <td colSpan={3}></td>
                                <td style={{ padding: 5, textAlign: "right" }}>Shipping</td>
                                <td style={{ padding: 5, textAlign: "right" }}>
                                    ${financial(props.order.shippingAmount)}
                                </td>
                            </tr>
                        )}
                        {props.order.fixedShipping > 0 && props.order.isPickupAtDealership !== 1 && (
                            <tr>
                                <td colSpan={3}></td>
                                <td style={{ padding: 5, textAlign: "right" }}>Fixed Shipping</td>
                                <td style={{ padding: 5, textAlign: "right" }}>
                                    ${financial(props.order.fixedShipping)}
                                </td>
                            </tr>
                        )}
                        <tr>
                            <td colSpan={3}></td>
                            <td style={{ padding: 5, textAlign: "right" }}>
                                {getTranslation("Sales tax", locale)} ({props.order.salesTaxRate}%)
                                {props.order &&
                                    props.order.salesTaxExceptionNo &&
                                    props.order.salesTaxExceptionNo.length > 0 &&
                                    props.order.staxEndDate &&
                                    props.order.staxEndDate.length > 0 &&
                                    Number(
                                        props.order && props.order.salesTaxAmount != null
                                            ? props.order.salesTaxAmount
                                            : 0
                                    ) === 0 && (
                                        <>
                                            <p style={{ marginTop: 0, marginBottom: 0 }}>
                                                {getTranslation("Sales tax exception number", locale)}:{" "}
                                                {props.order.salesTaxExceptionNo}
                                            </p>
                                            <p style={{ marginTop: 0, marginBottom: 0 }}>
                                                {getTranslation("Tax end date", locale)}:{" "}
                                                {props.order.staxEndDate}
                                            </p>
                                        </>
                                    )}
                            </td>
                            <td style={{ padding: 5, textAlign: "right" }}>
                                $
                                {financial(
                                    props.order.salesTaxAmount,
                                    props.order.billCountry,
                                    true
                                )}
                            </td>
                        </tr>
                        {props.order.handlingFeeAmount > 0 && (
                            <tr>
                                <td colSpan={3}></td>
                                <td style={{ padding: 5, textAlign: "right" }}>Handling Fee</td>
                                <td style={{ padding: 5, textAlign: "right" }}>
                                    ${financial(props.order.handlingFeeAmount)}
                                </td>
                            </tr>
                        )}
                        {props.order.customFeeAmount > 0 && (
                            <tr>
                                <td colSpan={3}></td>
                                <td style={{ padding: 5, textAlign: "right" }}>
                                    {props.order.customFeeTitle}
                                </td>
                                <td style={{ padding: 5, textAlign: "right" }}>
                                    ${financial(props.order.customFeeAmount)}
                                </td>
                            </tr>
                        )}
                        <tr>
                            <td colSpan={3}></td>
                            <td style={{ padding: 5, textAlign: "right" }}>Total</td>
                            <td style={{ padding: 5, textAlign: "right" }}>
                                $
                                {financial(
                                    (props.order?.subtotal || 0) +
                                    (props.order?.shippingAmount || 0) +
                                    (props.order?.fixedShipping || 0) +
                                    (props.order?.salesTaxAmount || 0) +
                                    (props.order?.handlingFeeAmount || 0) +
                                    (props.order?.customFeeAmount || 0)
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
                {props.order.userMemo && (
                    <div style={{ marginTop: 50 }}>
                        {getTranslation("User memo", locale)}: {props.order.userMemo}
                    </div>
                )}
                {showShippingSection && props.order.shipMemo && props.order.isPickupAtDealership !== 1 && (
                    <div style={{ marginTop: 50 }}>
                        {getTranslation("Shipping memo", locale)}: {props.order.shipMemo}
                    </div>
                )}
            </div>
        </div>
    );
});

ComponentToPrint.displayName = "ComponentToPrint";

export default function PrintOrder({ dealerId, id }) {
    const componentRef = useRef();
    const onBeforeGetContentResolve = React.useRef(null);
    const [order, setOrder] = useState({ orderNumber: "" });

    const handleOnBeforeGetContent = React.useCallback(() => {
        return new Promise((resolve) => {
            onBeforeGetContentResolve.current = resolve;
            OrderDataService.get(id).then((o) => {
                setOrder({
                    ...o,
                    products: o.details,
                });
                resolve();
            });
        });
    }, [setOrder]);

    return (
        <>
            <ReactToPrint
                trigger={() => {
                    return (
                        <Button
                            justIcon
                            round
                            simple
                            color="warning"
                            className="edit"
                            title="View order"
                        >
                            <PrintIcon />
                        </Button>
                    );
                }}
                content={() => componentRef.current}
                documentTitle={`Order number ${order.orderNumber}`}
                removeAfterPrint
                onBeforeGetContent={handleOnBeforeGetContent}
            />
            <div style={{ display: "none" }}>
                <ComponentToPrint order={order} locale={"en"} ref={componentRef} />
            </div>
        </>
    );
}
